import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, CheckCircle, ShieldCheck, ArrowLeft, GraduationCap, Certificate } from "@phosphor-icons/react";
import Seo, { elearningCourseSchema, breadcrumbSchema } from "@/components/Seo";

const STATE_NAMES = {
  CA: "California", DE: "Delaware", FL: "Florida", IA: "Iowa",
  ID: "Idaho", MO: "Missouri", OH: "Ohio", OR: "Oregon",
  TN: "Tennessee", TX: "Texas", WA: "Washington", WY: "Wyoming",
};

export default function ElearningDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/elearning/courses/${slug}`).then(({ data }) => setCourse(data)).catch(() => setCourse(false));
    api.get("/elearning/courses").then(({ data }) => setRelated(data)).catch(() => {});
  }, [slug]);

  if (course === false) {
    return (
      <div className="container-x py-32 text-center" data-testid="elearning-not-found">
        <h1 className="font-heading text-3xl mb-4">Course not found</h1>
        <Link to="/elearning" className="text-[#FFD300] font-mono text-xs tracking-[0.2em] uppercase">← Back to eLearning</Link>
      </div>
    );
  }
  if (!course) {
    return <div className="container-x py-32 text-center text-neutral-500 font-mono text-sm">Loading…</div>;
  }

  const launchCourse = async () => {
    setLaunching(true);
    try {
      const { data } = await api.post(`/elearning/click/${slug}`);
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch {
      // Fallback to direct URL if tracking fails
      window.open(course.url, "_blank", "noopener,noreferrer");
    } finally {
      setLaunching(false);
    }
  };

  const sameCategory = related.filter(r => r.category === course.category && r.slug !== course.slug).slice(0, 3);

  return (
    <div data-testid="elearning-detail-page">
      <Seo
        title={course.title}
        description={course.short_description}
        path={`/elearning/${course.slug}`}
        image={course.image}
        type="article"
        schema={[
          elearningCourseSchema({
            title: course.title,
            description: course.short_description,
            slug: course.slug,
            image: course.image,
          }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "eLearning", path: "/elearning" },
            { name: course.title, path: `/elearning/${course.slug}` },
          ]),
        ]}
      />
      {/* Sponsored top strip */}
      <div className="border-b border-white/10 bg-[#FFD300]/[0.06]" data-testid="lawshield-banner">
        <div className="container-x py-3 flex items-center justify-center gap-2 text-xs font-mono tracking-[0.15em] uppercase">
          <ShieldCheck size={14} weight="duotone" className="text-[#FFD300]" />
          <span>Sponsored by <span className="text-[#FFD300]">U.S. LawShield<sup>®</sup></span></span>
        </div>
      </div>

      <section className="border-b border-white/10 pt-12 pb-16">
        <div className="container-x">
          <Link to="/elearning" className="inline-flex items-center gap-2 text-neutral-400 font-mono text-[11px] tracking-[0.2em] uppercase mb-8 hover:text-[#FFD300]" data-testid="back-to-elearning">
            <ArrowLeft size={14} /> Back to eLearning
          </Link>

          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 lg:col-span-7">
              <div className="flex items-center gap-2 mb-5">
                <span className="code-badge">{course.category.toUpperCase()}</span>
                {course.state && <span className="state-tag">{course.state}</span>}
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter leading-[0.95] mb-6" data-testid="elearning-title">
                {course.title}
              </h1>
              <p className="text-neutral-300 leading-relaxed text-base mb-10">
                {course.long_description}
              </p>

              {course.highlights?.length > 0 && (
                <div className="surface-card p-7">
                  <div className="overline mb-5">Course Highlights</div>
                  <ul className="space-y-3" data-testid="course-highlights">
                    {course.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-3 text-neutral-200">
                        <CheckCircle size={18} weight="duotone" className="text-[#FFD300] mt-0.5 flex-shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Right rail */}
            <div className="col-span-12 lg:col-span-5">
              <div className="sticky top-24 space-y-6">
                <div className="aspect-[4/3] bg-[#0E0E0E] surface-card relative overflow-hidden">
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 left-3 font-mono text-[9px] tracking-[0.2em] uppercase bg-[#FFD300] text-black px-2 py-1">SPONSORED</div>
                  {course.state && (
                    <div className="absolute bottom-3 right-3 font-mono text-[10px] tracking-[0.18em] uppercase bg-black/70 backdrop-blur text-[#FFD300] px-2 py-1 border border-[#FFD300]/40">
                      {STATE_NAMES[course.state] || course.state}
                    </div>
                  )}
                </div>

                <div className="surface-card p-6">
                  <div className="overline mb-4">Included</div>
                  <ul className="text-sm text-neutral-300 space-y-2.5">
                    <li className="flex items-center gap-3"><GraduationCap size={18} weight="duotone" className="text-[#FFD300]" /> Full lifetime access</li>
                    <li className="flex items-center gap-3"><CheckCircle size={18} weight="duotone" className="text-[#FFD300]" /> Mobile and desktop</li>
                    <li className="flex items-center gap-3"><Certificate size={18} weight="duotone" className="text-[#FFD300]" /> Certificate of completion</li>
                  </ul>

                  <button
                    onClick={launchCourse}
                    disabled={launching}
                    data-testid="launch-course-btn"
                    className="mt-6 w-full bg-[#FFD300] text-black font-mono text-xs font-bold tracking-[0.18em] uppercase py-4 hover:bg-[#FFE149] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {launching ? "Opening…" : <>Launch Course on U.S. LawShield <ArrowRight size={14} weight="bold" /></>}
                  </button>
                  <p className="mt-3 text-[10px] font-mono tracking-[0.12em] uppercase text-neutral-500 text-center">
                    Opens at educate.uslawshield.com
                  </p>
                </div>

                <div className="text-[11px] text-neutral-500 leading-relaxed">
                  This course is hosted and delivered by U.S. LawShield® via their eLearning platform.
                  Pricing, content, and certifications are managed by U.S. LawShield®.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {sameCategory.length > 0 && (
        <section className="section-pad">
          <div className="container-x">
            <div className="overline mb-6">More {course.category}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
              {sameCategory.map(r => (
                <Link key={r.slug} to={`/elearning/${r.slug}`} className="bg-[#0A0A0A] p-6 lift block group" data-testid={`related-${r.slug}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="code-badge">{r.category.toUpperCase()}</span>
                    {r.state && <span className="state-tag">{r.state}</span>}
                  </div>
                  <h3 className="font-heading text-lg tracking-tight leading-tight mb-3">{r.title}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed line-clamp-2 mb-4">{r.short_description}</p>
                  <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#FFD300] flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                    View <ArrowRight size={12} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
